<template>
  <LanguageSwitcher :model-value="selectedLanguageCode" @change="changeLocale" :invert-color="invertColor" />

  <Teleport to="body">
    <div
      class="modal fade"
      id="languageChangeModal"
      tabindex="-1"
      aria-labelledby="languageChangeModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0">
          <div class="modal-header d-flex border-0 pb-0">
            <h5 id="languageChangeModalLabel" class="modal-title pe-3">{{ modalTitle }}</h5>
            <span v-if="modalState === 'loading'" class="spinner-border spinner-border-sm txt-primary" role="status" aria-hidden="true"></span>
          </div>
          <div class="modal-body">
            <div v-if="modalState === 'loading'" class="d-flex gap-3">
              <span>{{ t("global.header.language.pleaseWait") }}</span>
            </div>
            <p v-else-if="modalState === 'error'" class="text-danger mb-0">{{ modalMessage }}</p>
          </div>
          <div v-if="modalState === 'error'" class="modal-footer pt-0 border-0">
            <button type="button" class="btn btn-outline-default" @click="closeModal">
              {{ t("global.header.language.close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { Modal } from "bootstrap";

import { Trans } from "@/i18n";
import { apiClient, API_PATHS } from "@/services/api";
import LanguageSwitcher from "@/components/common/LanguageSwitcher.vue";

interface LocaleOption {
  value: string;
  name: string;
  short: string;
  lan: string;
  icon: string;
}

interface UserData {
  uid: string;
  language?: string;
  [key: string]: any;
}

defineProps<{ invertColor?: boolean }>();

const { t } = useI18n();

const selectedLanguageCode = ref<string>("en");
const modal = ref<InstanceType<typeof Modal> | null>(null);
const modalState = ref<"loading" | "error" | null>(null);
const modalMessage = ref<string>("");

const modalTitle = computed(() => {
  if (modalState.value === "loading") return t("global.header.language.changingLanguage");
  if (modalState.value === "error") return t("global.header.language.error");
  return "";
});

const closeModal = () => {
  modal.value?.hide();
};

const getUserData = (): UserData | null => {
  try {
    const data = localStorage.getItem("userAddData");
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

const updateLocalStorageUserData = (language: LocaleOption): void => {
  const userData = getUserData();
  if (userData) {
    localStorage.setItem(
      "userAddData",
      JSON.stringify({
        ...userData,
        language: language.value,
      })
    );
  }
};

const changeLocale = async (language: LocaleOption): Promise<void> => {
  modalState.value = "loading";
  modalMessage.value = "";
  modal.value?.show();

  try {
    const userData = getUserData();
    if (!userData?.uid) {
      throw new Error(t("global.header.language.updateError"));
    }

    await apiClient.rfp.put(API_PATHS.rfp.users.byId(userData.uid), {
      language: language.value,
      uid: userData.uid,
      creatorId: userData.uid,
    });

    updateLocalStorageUserData(language);
    selectedLanguageCode.value = language.value;

    await Trans.switchLanguage(language.value);

    // TODO: remove after all translations in the project were updated to use t() instead of direct string
    window.location.reload();
  } catch (error) {
    console.error("Error updating language:", error);
    modalState.value = "error";
    modalMessage.value =
      error instanceof Error ? error.message : t("global.header.language.updateError");
  }
};

onMounted(() => {
  selectedLanguageCode.value = Trans.getCurrentLanguage();

  const el = document.getElementById("languageChangeModal");
  if (el) {
    modal.value = new Modal(el);
    el.addEventListener("hidden.bs.modal", () => {
      modalState.value = null;
      modalMessage.value = "";
    });
  }
});
</script>
